import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, Loader2, X, Plus } from "lucide-react";

const CHUNK_SIZE = 1024 * 1024;
export const SPONSOR_TYPES = ["Title Sponsor", "Presenting Sponsor", "Gold Sponsor", "Silver Sponsor", "Bronze Sponsor", "Paddle Partner", "Venue Partner", "Food & Beverage", "Media Partner", "Community Partner"];

async function uploadLogo(file) {
  const uploadId = `sponsor_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  let res = null;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", i);
    fd.append("total_chunks", total);
    fd.append("filename", file.name);
    fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
    res = (await api.post("/community/upload/chunk", fd)).data;
  }
  return res.url.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + res.url : res.url;
}

export default function SponsorsEditor({ sponsors, onChange, input, lbl }) {
  const [draft, setDraft] = useState({ name: "", type: "", url: "", logo: null });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only images are supported here");
    if (file.size > 8 * 1024 * 1024) return toast.error("Logo is too big — max 8MB");
    setUploading(true);
    try { setDraft((d) => ({ ...d, logo: null })); const url = await uploadLogo(file); setDraft((d) => ({ ...d, logo: url })); }
    catch { toast.error("Logo upload failed"); }
    finally { setUploading(false); }
  };
  const add = () => {
    const name = draft.name.trim();
    if (!name) return toast.error("Enter the sponsor's name");
    if (sponsors.length >= 12) return toast.error("Up to 12 sponsors per event");
    onChange([...sponsors, { name, type: draft.type.trim() || "Sponsor", url: draft.url.trim() || null, logo: draft.logo || null }]);
    setDraft({ name: "", type: "", url: "", logo: null });
  };
  const remove = (i) => onChange(sponsors.filter((_, j) => j !== i));

  return (
    <div className="border border-pb-border bg-pb-elevated/30 p-3" data-testid="tcf-sponsors-section">
      <label className={lbl}>Event Sponsors <span className="text-zinc-600 normal-case tracking-normal">(shown on the tournament card & public event page)</span></label>
      {sponsors.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="tcf-sponsor-list">
          {sponsors.map((sp, i) => (
            <div key={i} className="flex items-center gap-2 border border-pb-border bg-pb-surface pl-2 pr-1.5 py-1.5" data-testid={`tcf-sponsor-${i}`}>
              {sp.logo ? <img src={sp.logo} alt="" className="h-6 w-6 object-contain bg-black" /> : <span className="h-6 w-6 grid place-items-center bg-pb-elevated text-[10px] font-black text-zinc-400">{sp.name[0]}</span>}
              <div className="leading-tight">
                <div className="text-xs text-white font-semibold">{sp.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-amber-400">{sp.type}</div>
              </div>
              <button type="button" data-testid={`tcf-sponsor-remove-${i}`} onClick={() => remove(i)} className="text-zinc-500 hover:text-red-400 ml-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1.2fr_auto_auto] gap-2 items-end">
        <div><label className={lbl}>Sponsor name</label>
          <input data-testid="tcf-sponsor-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={60} placeholder="Selkirk, Joe's Tacos…" className={input}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} /></div>
        <div><label className={lbl}>Sponsor type</label>
          <input data-testid="tcf-sponsor-type" list="tcf-sponsor-type-options" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} maxLength={40} placeholder="Title Sponsor, Gold…" className={input} />
          <datalist id="tcf-sponsor-type-options">{SPONSOR_TYPES.map((t) => <option key={t} value={t} />)}</datalist></div>
        <div><label className={lbl}>Website <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></label>
          <input data-testid="tcf-sponsor-url" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} maxLength={300} placeholder="https://…" className={input} /></div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="tcf-sponsor-logo-input" />
          {draft.logo ? (
            <div className="flex items-center gap-1"><img src={draft.logo} alt="" className="h-9 w-9 object-contain border border-pb-border bg-black" data-testid="tcf-sponsor-logo-preview" />
              <button type="button" onClick={() => setDraft({ ...draft, logo: null })} className="text-zinc-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button></div>
          ) : (
            <button type="button" data-testid="tcf-sponsor-logo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} title="Upload logo"
              className="h-9 px-3 border border-pb-border text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50 flex items-center gap-1.5 text-[10px] uppercase tracking-widest">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Logo
            </button>
          )}
        </div>
        <button type="button" data-testid="tcf-sponsor-add" onClick={add} disabled={uploading}
          className="h-9 px-4 bg-pb-red text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
    </div>
  );
}
